import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../../contexts/AuthContext';

export function Login({ onLogin }: { onLogin: () => void }) {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [tentativas, setTentativas] = useState(0);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Inicializa áudio MP3 apenas uma vez (mutado no início)
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio("/3milhoes.mp3");
      audioRef.current.loop = true;
      audioRef.current.currentTime = 35; // começa do segundo 35
      audioRef.current.volume = 0.5;
      audioRef.current.muted = true; // começa mutado
      audioRef.current.play().catch(() => {});
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // Ativa o som quando o usuário clicar em um campo
    const ativarSom = () => {
    if (audioRef.current) {
      if (audioRef.current.currentTime < 30) {
        audioRef.current.currentTime = 40; // ⏩ adianta o áudio para 35s
      }
      audioRef.current.muted = false;
      audioRef.current.play().catch(() => {});
    }
  };

  // Tenta iniciar o vídeo automaticamente
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch((err) => {
        console.warn("Autoplay bloqueado pelo navegador:", err);
      });
    }
  }, []);

  const handleLogin = async () => {
    try {
      await login(email, senha);
      if (onLogin) onLogin();
      navigate('/');
    } catch (err) {
      const novaTentativa = tentativas + 1;
      setTentativas(novaTentativa);
      if (novaTentativa >= 2) navigate('/bloqueado');
      else alert('Credenciais inválidas. ' + (err instanceof Error ? err.message : 'Tente novamente.'));
    }
  };

  return (
    <>
      {/* Vídeo de fundo ou imagem fallback */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          zIndex: -2,
          background: "linear-gradient(45deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        }}
      >
        {!videoError ? (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onLoadedData={() => setVideoLoaded(true)}
            onError={() => {
              console.error("Erro ao carregar vídeo de fundo: verifique se /3milhoesvideo.mp4 está em /public");
              setVideoError(true);
              setVideoLoaded(false);
            }}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: videoLoaded ? 1 : 0,
              transition: "opacity 1s ease",
              visibility: videoLoaded ? "visible" : "hidden",
            }}
          >
            <source src="/3milhoesvideo.mp4" type="video/mp4" />
            <source src="/3milhoesvideo.webm" type="video/webm" />
            Seu navegador não suporta vídeo.
          </video>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              backgroundImage: "url('/fallback.jpg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        )}
      </Box>

      {/* Overlay escura */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(0,0,0,0.6)",
          zIndex: -1,
        }}
      />

      {/* Container de login */}
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 2,
          padding: 2,
          color: "#eee",
          userSelect: "none",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            backgroundColor: "#0d0d0dcc",
            color: "white",
            padding: 5,
            width: 380,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
            backdropFilter: "blur(12px)",
            boxShadow: "0 0 30px rgba(0,0,0,0.9)",
          }}
        >
          <Typography
            variant="h4"
            textAlign="center"
            sx={{ fontWeight: 700, textShadow: "0 0 12px #3b5998, 0 0 25px #3b5998" }}
          >
            Bem-Vindo(a)
          </Typography>

          <Typography variant="subtitle1" textAlign="center" color="#bbb">
            Efetue seu login abaixo:
          </Typography>

          <TextField
            label="E-mail/Usuário"
            placeholder="Digite seu E-mail/Usuário..."
            variant="filled"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onClick={ativarSom} // 🔊 ativa som ao clicar
            InputProps={{
              disableUnderline: true,
              sx: { backgroundColor: "#222", borderRadius: 2, color: "white", "& input": { color: "white" } },
            }}
            InputLabelProps={{ sx: { color: "#888" } }}
          />

          <TextField
            label="Senha"
            placeholder="Digite sua senha..."
            type={showPassword ? "text" : "password"}
            variant="filled"
            fullWidth
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            onClick={ativarSom} // 🔊 ativa som ao clicar
            InputProps={{
              disableUnderline: true,
              sx: { backgroundColor: "#222", borderRadius: 2, color: "white", "& input": { color: "white" } },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: "#aaa" }}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ sx: { color: "#888" } }}
          />

          <Box textAlign="right">
            <Link href="#" underline="hover" color="#888" fontSize={14}>
              Esqueceu a senha?
            </Link>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={handleLogin}
            sx={{
              backgroundColor: "#3b5998",
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: "bold",
              paddingY: 1.5,
              transition: "background-color 0.3s ease",
              "&:hover": { backgroundColor: "#36579eff" },
            }}
          >
            Login
          </Button>
        </Paper>
      </Box>
    </>
  );
}
